import { formatPrice } from '../currency';
import { Skeleton } from '../design-system';

/** What the banner says about the saving: a quote ("You save"), a delivery
 *  that was charged ("You saved"), or a delivery that was canceled or refunded,
 *  where nothing was saved in the end and the amount is left out. */
export type PromoBannerMode = 'quote' | 'charged' | 'refunded';

export interface PromoBannerProps {
  /** The promotion's name. */
  label: string;
  /** What the promotion takes off the regular shipping and handling fees. */
  discount: number;
  mode?: PromoBannerMode;
  /** A new quote is in flight: the banner stays put (so the page does not
   *  jump) with a loader where the saving was, instead of a stale amount. */
  loading?: boolean;
  /** For a page that does not set its own text colour: the label takes it. */
  className?: string;
}

// The green of the "Default" address badge. A saving is good news, so it must
// not take the brand accent, which can be red (modern, sleek) and read as an error.
const POSITIVE = 'var(--co-DefaultBadge-fg, #16B814)';
const POSITIVE_FILL = 'var(--co-DefaultBadge-bg, rgba(34,225,28,0.16))';

/**
 * The promotion indicator: a tinted, green-bordered banner that names the
 * promotion and says what it saves, shown wherever a price a promotion lowered
 * is shown, so the struck-through figures never appear unexplained.
 */
export function PromoBanner({
  label,
  discount,
  mode = 'quote',
  loading = false,
  className = '',
}: PromoBannerProps) {
  const saving = mode === 'refunded' ? '' : formatPrice(discount, 2);

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-[14px] border px-3.5 py-2.5 ${className}`.trimEnd()}
      style={{ borderColor: POSITIVE, backgroundColor: POSITIVE_FILL }}
    >
      <span className="min-w-0 break-words text-[14px] font-semibold leading-[20px]">
        <span style={{ color: POSITIVE }}>Promotion:</span> {label}
      </span>
      {loading ? (
        <Skeleton className="h-[20px] w-[96px] rounded" />
      ) : saving ? (
        <span
          className="whitespace-nowrap text-[15px] font-bold leading-[20px] tabular-nums"
          style={{ color: POSITIVE }}
        >
          {mode === 'charged' ? 'You saved' : 'You save'} {saving}
        </span>
      ) : null}
    </div>
  );
}
